import type {
  ActionCategory,
  ActionCommand,
  EnterStockMode,
  ProductResult,
  SearchResultCommand,
  WorkflowTransition
} from '@/domains/command-center/types'

import {
  STOCK_CATEGORY_ID,
  STOCK_MODE_NAME,
  UPDATE_STOCK_ACTION_ID,
  UPDATE_STOCK_FOR_MATCH_ID
} from '@/domains/command-center/handlers/stock/stockMode.consts'
import { isProductResult } from '@/domains/command-center/utils/searchResultUtils'

const STOCK_ICON = 'swap_vert'

const getStockCategory = (): ActionCategory => ({
  id: STOCK_CATEGORY_ID,
  label: 'Stock',
  // Stock mode searches products and saves through the single adjust, so it asks for both. A role
  // with one of the two would find nothing to adjust, or meet a 403 on the save.
  permission: [
    { action: 'view', subject: 'catalog' },
    { action: 'adjust', subject: 'inventory.stock' }
  ],
  actions: [
    {
      id: UPDATE_STOCK_ACTION_ID,
      type: 'action',
      label: STOCK_MODE_NAME,
      icon: STOCK_ICON,
      keywords: ['stock', 'update', 'adjust', 'count', 'receive', 'delivery', 'quantity', 'inventory'],
      execute: (): EnterStockMode => ({ type: 'enterStockMode' })
    }
  ]
})

/**
 * The one product whose code is exactly the query, or null.
 *
 * Exactly one, because the row names a product. A code on two products is ambiguous, and the plain
 * command still reaches both.
 */
const singleScannedProduct = (results: SearchResultCommand[], query: string | undefined): ProductResult | null => {
  const code = query?.trim().toLowerCase()
  if (!code) return null

  const matches = results
    .filter(isProductResult)
    .filter(result => result.skuMatch?.hasMatch === true && result.skuMatch.matchedSku?.toLowerCase() === code)

  return matches.length === 1 ? matches[0] : null
}

/**
 * "Update Stock for {product}", offered when a scan names one product.
 *
 * A scan is how a delivery is checked in, so this row is the fast way into stock mode. It is built
 * from the results rather than declared on the category, so the query never scores it: a code scores
 * nothing against the row's label.
 */
const createUpdateStockForMatchAction = (
  results: SearchResultCommand[],
  query: string | undefined
): ActionCommand | null => {
  const match = singleScannedProduct(results, query)
  if (!match) return null

  const product = match.originalData

  return {
    id: UPDATE_STOCK_FOR_MATCH_ID,
    type: 'action',
    // Title case, as every palette command is: "Update Stock", "Create New Product".
    label: `${STOCK_MODE_NAME} for ${product.title}`,
    subLabel: match.subLabel,
    icon: STOCK_ICON,
    execute: (): EnterStockMode => ({ type: 'enterStockMode', product, code: match.skuMatch?.matchedSku })
  }
}

/** Typed on what a command can answer, so a misspelt `type` fails to compile. */
const isEnterStockMode = (result: void | WorkflowTransition | EnterStockMode): result is EnterStockMode =>
  result?.type === 'enterStockMode'

export { createUpdateStockForMatchAction, getStockCategory, isEnterStockMode }
